import { useMemo, useState } from "react";

import { MetricBlock } from "../api";
import PlayerCell from "../components/PlayerCell";
import ProgressBar from "../components/ProgressBar";
import { classFor } from "../data/classes";

interface ItemStats {
  damage: number;
  count: number;
}

interface SourceRow {
  name?: string;
  type?: string;
  icon?: string;
  total: number;
  totalCount: number;
  byItem: Record<string, ItemStats>;
}

interface TableRow {
  name?: string;
  type?: string;
  icon?: string;
  damage: number;
  count: number;
}

const fmt = (n: number) => n.toLocaleString("en-US");
const fmtCompact = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(2)}M`
    : n >= 1_000
      ? `${(n / 1_000).toFixed(1)}k`
      : `${Math.round(n)}`;

/**
 * Tabbed engineering-damage view. Same pattern as Boss Damage —
 * single table whose contents come from the active tab. "Total"
 * shows raid-wide engineering totals; remaining tabs are items
 * sorted by raid contribution (damage first, then uses).
 */
export default function EngineeringDamage({ block }: { block: MetricBlock }) {
  const rows = (block.rows as unknown as SourceRow[]) ?? [];

  const itemSummary = useMemo(() => {
    const out: Record<string, ItemStats> = {};
    for (const r of rows) {
      for (const [item, stats] of Object.entries(r.byItem ?? {})) {
        const acc = out[item] ?? { damage: 0, count: 0 };
        acc.damage += stats.damage;
        acc.count += stats.count;
        out[item] = acc;
      }
    }
    return out;
  }, [rows]);

  const items = useMemo(
    () =>
      Object.entries(itemSummary).sort((a, b) => {
        // Damage first, then uses, so utility items (low damage but
        // many uses) still appear in a predictable order.
        if (b[1].damage !== a[1].damage) return b[1].damage - a[1].damage;
        return b[1].count - a[1].count;
      }),
    [itemSummary],
  );

  const [selected, setSelected] = useState<"total" | string>("total");

  const raidTotals = (block.totals ?? {}) as {
    raidTotal?: number;
    raidCount?: number;
  };

  const { tableRows, title, subtitle, totalDamage, totalCount } = useMemo(() => {
    if (selected === "total") {
      const r = [...rows]
        .map((row) => ({
          name: row.name,
          type: row.type,
          icon: row.icon,
          damage: row.total,
          count: row.totalCount,
        }))
        .filter((row) => row.damage > 0 || row.count > 0)
        .sort((a, b) => b.damage - a.damage || b.count - a.count);
      return {
        tableRows: r,
        title: "Total Engineering Damage",
        subtitle: `${items.length} item${items.length === 1 ? "" : "s"} used`,
        totalDamage: raidTotals.raidTotal ?? 0,
        totalCount: raidTotals.raidCount ?? 0,
      };
    }
    const r = rows
      .map((row) => {
        const stats = row.byItem[selected];
        return {
          name: row.name,
          type: row.type,
          icon: row.icon,
          damage: stats?.damage ?? 0,
          count: stats?.count ?? 0,
        };
      })
      .filter((row) => row.damage > 0 || row.count > 0)
      .sort((a, b) => b.damage - a.damage || b.count - a.count);
    const summary = itemSummary[selected] ?? { damage: 0, count: 0 };
    return {
      tableRows: r,
      title: selected,
      subtitle: undefined,
      totalDamage: summary.damage,
      totalCount: summary.count,
    };
  }, [selected, rows, items, itemSummary, raidTotals]);

  if (!rows.length || !items.length) {
    return (
      <p className="text-sm text-gray-500">
        No engineering activity detected. (Check ability IDs in{" "}
        <code>backend/app/data/engineering.json</code>; if you expected some,
        re-import the log so the new data flows through.)
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <Tabs items={items} selected={selected} onSelect={setSelected} />

      <ItemTable
        title={title}
        subtitle={subtitle}
        totalDamage={totalDamage}
        totalCount={totalCount}
        rows={tableRows}
      />
    </div>
  );
}

/* ----------------------------- Tabs ----------------------------- */

function Tabs({
  items,
  selected,
  onSelect,
}: {
  items: [string, ItemStats][];
  selected: "total" | string;
  onSelect: (s: "total" | string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <Tab
        active={selected === "total"}
        onClick={() => onSelect("total")}
        label="Total"
      />
      {items.map(([item, stats]) => (
        <Tab
          key={item}
          active={selected === item}
          onClick={() => onSelect(item)}
          label={item}
          aside={`${fmtCompact(stats.damage)} · ×${stats.count}`}
        />
      ))}
    </div>
  );
}

function Tab({
  active,
  onClick,
  label,
  aside,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  aside?: string;
}) {
  return (
    <button
      onClick={onClick}
      title={aside ? `${label} · ${aside}` : label}
      className={[
        "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium",
        "transition-colors max-w-[16rem]",
        active
          ? "border-accent/40 bg-accent/15 text-accent"
          : "border-border/60 bg-bg/40 text-gray-300 hover:bg-white/5 hover:text-gray-100",
      ].join(" ")}
    >
      <span className="truncate">{label}</span>
      {aside && (
        <span className="text-[10px] tabular-nums text-gray-500 flex-shrink-0">
          {aside}
        </span>
      )}
    </button>
  );
}

/* ----------------------------- Table ----------------------------- */

function ItemTable({
  title,
  subtitle,
  totalDamage,
  totalCount,
  rows,
}: {
  title: string;
  subtitle?: string;
  totalDamage: number;
  totalCount: number;
  rows: TableRow[];
}) {
  if (!rows.length) {
    return (
      <section className="rounded border border-border/60 bg-bg/40 px-4 py-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="text-xs text-gray-500 mt-1">No data.</p>
      </section>
    );
  }

  const top = rows[0]?.damage ?? 0;
  const rankPad = String(rows.length).length;

  return (
    <section className="rounded border border-border/60 bg-bg/40">
      <header className="flex items-baseline justify-between gap-2 px-4 pt-3 pb-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        <div className="text-xs text-gray-500 flex items-baseline gap-3">
          {subtitle && <span>{subtitle}</span>}
          <span className="tabular-nums">
            <span className="text-gray-300 font-medium">{fmt(totalDamage)}</span>
            {" damage"}
          </span>
          <span className="tabular-nums">
            <span className="text-gray-300 font-medium">{totalCount}</span>
            {" use"}
            {totalCount === 1 ? "" : "s"}
          </span>
        </div>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
              <th className="pb-2 pl-4 pr-3 font-medium">#</th>
              <th className="pb-2 pr-3 font-medium">Player</th>
              <th className="pb-2 pr-3 font-medium text-right">Damage</th>
              <th className="pb-2 pr-3 font-medium text-right">Uses</th>
              <th className="pb-2 pr-4 font-medium w-1/3">% of best</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const pct = top ? (r.damage / top) * 100 : 0;
              const cls = classFor(r.type);
              const padded = String(i + 1).padStart(rankPad, "0");
              return (
                <tr
                  key={`${r.name}-${i}`}
                  className="border-t border-border/40 hover:bg-white/5 transition-colors"
                >
                  <td className="py-1.5 pl-4 pr-3 text-gray-500 tabular-nums">
                    {padded}
                  </td>
                  <td className="py-1.5 pr-3 max-w-[16rem]">
                    <PlayerCell name={r.name} type={r.type} icon={r.icon} />
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums font-medium">
                    {fmt(r.damage)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-gray-400">
                    {r.count}
                  </td>
                  <td className="py-1.5 pr-4">
                    <ProgressBar pct={pct} color={cls.color} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
